import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { ChangeEvent, FC, Key, ReactNode } from "react";

import { IconAlert } from "@/modules/irohana-components/assets/icons";
import { useFormField } from "@/modules/irohana-components/elements/FormField";
import { RadioNew } from "@/modules/irohana-components/elements/RadioNew";
import { Tooltip } from "@/modules/irohana-components/elements/Tooltip";

export type TRadioGroupNewOption = {
  value: Key;
  label: string;
  subLabel?: ReactNode;
  helper?: string;
  disabled?: boolean;
  labelColor?: string;
};

type TRadioGroupNewProps = {
  options: TRadioGroupNewOption[];
  nonControl?: boolean;
  direction?: "row" | "column";
  enableOrderNumber?: boolean;
  value?: Key;
  onChange?: (value: Key) => void;
  disabled?: boolean;
  disableFieldOnChangeForm?: boolean;
  labelSx?: SxProps<Theme>;
  formControlLabelSx?: SxProps<Theme>;
};

export const RadioGroupNew: FC<TRadioGroupNewProps> = ({
  options,
  nonControl = false,
  value,
  direction = "row",
  onChange,
  disabled,
  enableOrderNumber,
  disableFieldOnChangeForm = false,
  labelSx,
  formControlLabelSx,
}) => {
  const { field, fieldState } = useFormField(nonControl);

  const checkedValue = field?.value ?? value;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    if (checked) {
      if (!disableFieldOnChangeForm) {
        field?.onChange(value);
        field?.onBlur();
      }
      onChange?.(value);
    }
  };

  return (
    <Stack flexWrap="wrap" direction={direction} gap={direction === "row" ? 3 : 1}>
      {options.map(({ label, subLabel, value, helper, labelColor, disabled: optionDisabled }, index) => {
        return (
          <Stack key={value} direction="row" gap={0.5} alignItems="center">
            <RadioNew
              error={!!fieldState?.error}
              name={field?.name}
              label={label}
              prefixLabel={enableOrderNumber ? `${index + 1}.` : undefined}
              subLabel={subLabel as string}
              value={value}
              labelColor={labelColor}
              checked={checkedValue === value}
              onChange={handleChange}
              nonControl
              disabled={disabled || optionDisabled}
              labelSx={labelSx}
              formControlLabelSx={formControlLabelSx}
            />
            {helper && (
              <Box>
                <Tooltip
                  title="補足"
                  content={
                    <Typography variant="body14" whiteSpace="pre-line">
                      {helper}
                    </Typography>
                  }
                >
                  <IconAlert />
                </Tooltip>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
